import { noop } from 'lodash';

import {
   RefundConfigurationDetailsParameters,
   RefundConfigurationSetCommand,
} from '@dreryk/egabinet-appointment-advance-payment-frontend-api';

import { Button, ButtonVariant, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { Choice } from '@dreryk/watermelon';

import { serviceSettingsTranslationKeys } from '../../translations';

import {
   AdvancePaymentsSettingsFormBean,
   AdvancePaymentsSettingsFormController,
} from './AdvancePaymentsSettingsForm.types';
import { refundTypeVisitor } from './AdvancePaymentsSettingsForm.utils';

const PERCENTAGE_OF_FULL_REFUND = 100;

const t = serviceSettingsTranslationKeys.advancePayments.form.actions;

type AdvancePaymentsSettingsSaveButtonProps = Pick<AdvancePaymentsSettingsFormController, 'handleSubmit'> & {
   onSuccess: VoidFunction;
};

export const AdvancePaymentsSettingsSaveButton = (props: AdvancePaymentsSettingsSaveButtonProps): JSX.Element => {
   const { executeAsync: saveAppointmentRequestsSettings } = useCqrsCommand<void, AdvancePaymentsSettingsFormBean>(
      (parameters) => {
         const command = new RefundConfigurationSetCommand();
         command.automaticRefundEnabled = parameters.automaticRefundEnabled;

         refundTypeVisitor(parameters)({
            visitFullRefund: ({ refundType, fullRefundThresholdInHours }) => {
               command.refundConfigurationDetailsParameters = Choice.of<RefundConfigurationDetailsParameters>({
                  refundType: refundType,
                  timeBasedRefundDetailsParameters: [
                     {
                        refundPercentage: PERCENTAGE_OF_FULL_REFUND,
                        thresholdInHours: fullRefundThresholdInHours,
                     },
                  ],
               });
            },
            visitTimeBasedRefund: ({ refundType, timeBasedRefund }) => {
               command.refundConfigurationDetailsParameters = Choice.of<RefundConfigurationDetailsParameters>({
                  refundType: refundType,
                  timeBasedRefundDetailsParameters: timeBasedRefund.map(({ refundPercentage, thresholdInHours }) => ({
                     refundPercentage: refundPercentage!,
                     thresholdInHours: thresholdInHours!,
                  })),
               });
            },
            visitWithoutAutomaticRefund: noop,
         });

         return command;
      },
      {
         onSuccess: () => {
            props.onSuccess();
            dispatchSnackbarPositiveEvent(t.save.successMessage);
         },
      }
   );

   return (
      <Button
         testId={'advancePaymentsSettingsSave'}
         label={t.save.label}
         onClick={props.handleSubmit(saveAppointmentRequestsSettings)}
         variant={ButtonVariant.PRIMARY}
         compact
      />
   );
};
